import { Component, OnInit } from '@angular/core';
import {DbActiveModal} from "../../../modules/modal/modal-ref";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {NotifierComponent} from "../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../shared/components/notifier/notification";
import {NotificationType} from "../../../shared/components/notifier/notification-type";
import {CreateWebsiteHelpersService} from "../../../services/local/create-website-helpers.service";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../store";
import {OptionRegion} from "../../../shared/models/option-region.model";
import {HostingService} from "../../../websites/website/hosting/hosting.service";
import {getWebsite} from "../../../store";
import {CreateWebsiteSuccessComponent} from "../create-website-success/create-website-success.component";
import {DbModalService} from "../../../modules/modal/modal";
import {combineLatest, Subject} from "rxjs";
import {User} from "../../../user/user";
import {UserSubscription} from "../../../user/user-subscription";
import {Utils} from "../../../shared/utils/utils";
import {BuilderSectionsInteractionService} from "../../../builder-v2/builder-sections-interaction.service";

type ICreateWebsiteTrialStep = 'install' | 'creationProcess';

@Component({
    selector: 'db-create-website-trial',
    templateUrl: './create-website-trial.component.html',
    styleUrls: ['./create-website-trial.component.scss'],
    standalone: false
})
export class CreateWebsiteTrialComponent implements OnInit {

  public initialDataLoading: boolean = true;
  public step: ICreateWebsiteTrialStep = 'install';
  public creationProcessStep: string;
  public regions: OptionRegion[] = [];
  public hostedWebsitesLimit = 0;
  public hostedWebsitesCount = 0;
  public user: User;
  public currentSubscription: UserSubscription;
  public demoUser: boolean;
  companyName: string = '';

  private _destroyStream$: Subject<void> = new Subject<void>();
  constructor(
    private _activeModal: DbActiveModal,
    private _modalService: DbModalService,
    private _store: Store<fromStore.DbStoreState>,
    private _createWebsiteHelpersService: CreateWebsiteHelpersService,
    private _hostingService: HostingService,
    private _builderSectionsInteractionService: BuilderSectionsInteractionService,
  ) { }

  ngOnInit() {
    this._activeModal.resize(374, 700);
    this._activeModal.whiteCloseBtn(false);
    this._getUserAndSubscriptionData();
    this._getRegions();
    this._getHostedWebsitesEntities();
    this._getCompanyInfo();
  }

    private _getCompanyInfo(): void {
        this._builderSectionsInteractionService.companyInfo
            .pipe(
                filter(companyInfo => !!companyInfo),
                takeUntil(this._destroyStream$),
                first()
            )
            .subscribe(companyInfo => {
                this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
            });
    }

  private _getRegions(): void {
    this._store.select(fromStore.getUserSubscription)
      .pipe(first(subscription => !!subscription))
      .subscribe(subscription => {
        this._createWebsiteHelpersService.getRegions(subscription.subscriptionId).then(regions => {
          this.regions = regions;
          this.initialDataLoading = false;
        });
      });
  }

  private _getUserAndSubscriptionData(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription)
        )),
      )
      .subscribe(([user, currentSubscription]) => {
        this.user = user;
        this.currentSubscription = currentSubscription;
        this.demoUser = Utils.isDemoUser(currentSubscription, user);
      });
  }

  private _getHostedWebsitesEntities(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription),
          this._store.select(fromStore.getWebsites)
        )),
        first()
      )
      .subscribe(([user, userSubscription, websites]) => {
        this.hostedWebsitesLimit = user && user.hosting_services && user.hosting_services.domain_count && userSubscription && userSubscription.plan ? parseInt(user.hosting_services.domain_count.parameter_value, 10) + user.additionalWebsiteCount : null;
        this.hostedWebsitesCount = websites.filter(website => !website.parentId && website.isActive && website.type === 'live' && !!website.domain_hash).length;
      });
  }

  public createWebsite(websiteData): void {
    if (!websiteData) {
      return;
    }

    this.step = 'creationProcess';
    this._hostingService.createWebsiteForTrialUsers(websiteData, this.demoUser)
      .pipe(
        first(response => !!response),
      )
      .subscribe(response => {
        if (!response.success) {
          this.step = 'install';
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        } else {
          this._store.dispatch(new fromStore.LoadWebsites());
          this.creationProcessStep = 'done';
          /** 'done' step visualization p.s. only for UX */
          setTimeout(() => {
            this._showCreateWebsiteSuccess(response.data.domain_id);
          }, 1000);
        }
      });
  }

  private _showCreateWebsiteSuccess(websiteId: number): void {
    this._store.select(getWebsite(websiteId))
      .pipe(
        first(website => !!website)
      )
      .subscribe(website => {
        this._activeModal.close();
        const modal = this._modalService.open(CreateWebsiteSuccessComponent);
        modal.componentInstance.website = website;
      });
  }

}
